import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "roofing-ppc-colorado",
  metaTitle: "Roofing PPC & Google Ads in Colorado | Storm-Season Lead Gen",
  metaDescription:
    "Profitable Google Ads and Local Services Ads for Colorado roofing companies — storm response, hail damage, and insurance-claim lead flow. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Roofing SEO", to: "/roofing-seo-colorado" },
  ],
  currentLabel: "Roofing PPC Colorado",
  eyebrow: "Roofing PPC — Colorado",
  h1: (
    <>
      Roofing <span className="text-brand-gold">PPC</span> in Colorado
    </>
  ),
  heroDescription:
    "Insurance-claim lead flow on demand — Google Ads and LSAs tuned for Colorado storm season and built around your true cost-per-job math.",
  intro:
    "Colorado roofing PPC is won or lost in the 72 hours after a hailstorm. We run Google Search, Local Services Ads, and Performance Max campaigns built around your actual job economics — not vanity click metrics. Every campaign is geo-fenced to the storm path, bid-adjusted by neighborhood, and tracked all the way to signed contracts so you know your true cost per job.",
  definition: {
    term: "Roofing PPC in Colorado",
    body: "Roofing PPC is the practice of running Google Search Ads, Local Services Ads (LSAs), and Performance Max campaigns to drive insurance-claim and replacement leads for roofing companies — especially during Colorado hail season.",
  },
  whatWeDo: [
    { title: "Storm-response campaign builds", body: "Pre-built campaigns we activate within hours of a Colorado storm event — geo-fenced to the affected hail path." },
    { title: "Local Services Ads management", body: "LSA setup, badge optimization, weak-lead disputes, and category tuning to maximize qualified storm-damage calls." },
    { title: "Insurance-claim landing pages", body: "Conversion-tested pages built specifically for hail-damage and insurance-claim intent — not generic 'roofing services' pages." },
    { title: "Real cost-per-job tracking", body: "We track ad clicks → form fills → inspections → signed contracts so you see actual cost-per-job, not just cost-per-click." },
    { title: "Bid adjustments by neighborhood", body: "We bid up in storm-impact zip codes and bid down where your install crews aren't routing efficiently." },
    { title: "Call tracking and recording", body: "Every PPC call recorded and scored so you know which campaigns drive booked inspections vs. tire-kickers." },
  ],
  whyThisMarket: [
    {
      heading: "Hail season is a 90-day window",
      body: "Most Colorado hail events generate 60–90 days of elevated search volume. Roofers who pre-stage their PPC campaigns and activate fast capture disproportionate share. Roofers who scramble to set up campaigns miss the peak.",
    },
    {
      heading: "Insurance-claim leads have unique economics",
      body: "An insurance-claim job in Colorado can run $15K–$40K. CPC of $50 is rational when your average job is $25K — but only if you track all the way to signed contract. Most roofing PPC accounts measure clicks, not jobs.",
    },
    {
      heading: "LSAs and Search Ads compound when run together",
      body: "LSA leads are pay-per-lead and badge-driven. Search Ads are pay-per-click and intent-driven. Running both captures both buying motions and stabilizes lead flow across storm and shoulder seasons.",
    },
  ],
  relatedLinks: [
    { to: "/roofing-seo-colorado", label: "Roofing SEO in Colorado" },
    { to: "/colorado-springs-ppc", label: "How our Google Ads process works" },
    { to: "/roofing-google-maps-colorado", label: "Roofing Google Maps optimization" },
    { to: "/roofing-web-design-colorado", label: "Roofing web design in Colorado" },
    { to: "/colorado-springs-ppc", label: "Colorado Springs PPC" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/case-studies", label: "Colorado roofing case studies" },
    { to: "/google-ads-ppc", label: "Google Ads / PPC service (all industries)" },
    { to: "/pricing", label: "Pricing" },
  ],
  faqs: [
    { q: "What's a reasonable roofing PPC budget in Colorado?", a: "Most Colorado roofers run $3K–$15K/month outside storm windows and 2–3x that during peak hail season. The right budget depends on your install capacity — we'd rather run a smaller, profitable campaign than burn budget you can't service." },
    { q: "What's a good cost-per-lead for Colorado roofing?", a: "Storm-season CPL on Search Ads runs $80–$200, with LSAs typically $40–$120 per qualified call. The number that matters is cost-per-signed-contract, which we track end-to-end." },
    { q: "Can you activate campaigns fast after a storm?", a: "Yes — for clients on retainer we have pre-built storm-response campaigns staged and ready. We can activate geo-fenced campaigns within hours of a confirmed Colorado hail event." },
    { q: "Do you only serve Colorado Springs roofers?", a: "No — we serve roofing companies across all of Colorado: Denver, Aurora, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range corridor." },
  ],
  serviceName: "Roofing PPC in Colorado",
  serviceDescription:
    "Google Ads, Local Services Ads, and storm-response PPC campaigns for roofing companies across Colorado.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/roofing-ppc-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
